'use client';

import { useMemo, useState } from 'react';
import { computeStats, formatNumber, parseNumberList } from '@/lib/browser-mathcalc-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserAverageCalculator() {
  const [input, setInput] = useState('12, 7, 9, 12, 15, 3');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const { numbers, ignored } = useMemo(() => parseNumberList(input), [input]);
  const stats = useMemo(() => computeStats(numbers), [numbers]);

  const rows = stats
    ? [
        { label: 'Mean (average)', value: formatNumber(stats.mean, 6) },
        { label: 'Median', value: formatNumber(stats.median, 6) },
        { label: 'Mode', value: stats.mode },
        { label: 'Range', value: formatNumber(stats.range, 6) },
        { label: 'Sum', value: formatNumber(stats.sum, 6) },
        { label: 'Count', value: String(stats.count) },
        { label: 'Minimum', value: formatNumber(stats.min, 6) },
        { label: 'Maximum', value: formatNumber(stats.max, 6) },
        { label: 'Population std dev (σ)', value: formatNumber(stats.populationStdDev, 6) },
        { label: 'Sample std dev (s)', value: formatNumber(stats.sampleStdDev, 6) },
      ]
    : [];

  async function handleCopy() {
    if (!stats) return;
    try {
      await copyText(rows.map((row) => `${row.label}: ${row.value}`).join('\n'));
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the values.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Statistics</span>
        <h2 className="tool-live-title">Average Calculator</h2>
        <p className="tool-live-subtitle">
          Paste a list of numbers to get the mean, median, mode, range and standard deviation. Runs
          live in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Numbers (comma, space or newline separated)</span>
              <textarea
                onChange={(event) => setInput(event.target.value)}
                placeholder="12, 7, 9, 12, 15, 3"
                rows={8}
                value={input}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!stats}
              onClick={handleCopy}
              type="button"
            >
              Copy results
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {ignored.length > 0 ? (
            <p className="tool-live-panel-note">
              Ignored {ignored.length} non-numeric {ignored.length === 1 ? 'token' : 'tokens'}:{' '}
              {ignored.slice(0, 8).join(', ')}
              {ignored.length > 8 ? '…' : ''}
            </p>
          ) : null}
        </section>

        <section className="tool-live-preview">
          {stats ? (
            <>
              <div className="tool-html-meta-grid">
                {rows.map((row) => (
                  <div className="tool-live-field" key={row.label}>
                    <span>{row.label}</span>
                    <input readOnly type="text" value={row.value} />
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">Calculated locally in your browser.</p>
            </>
          ) : (
            <p className="tool-live-panel-note">Enter at least one number to see statistics.</p>
          )}
        </section>
      </div>
    </div>
  );
}
